"use client";
/**
 * PrefsSyncGate — hydrates localStorage from the server's user_prefs
 * before the app renders, so workspace layouts and chart config
 * follow the user across browsers / devices.
 *
 * Why a gate: the workspaces and chart read localStorage
 * SYNCHRONOUSLY when they mount. To make them transparently see the
 * server copy without touching their code, we must finish pulling
 * server prefs INTO localStorage before they mount. So this component
 * blocks its children until `hydratePrefs()` resolves.
 *
 * Login-aware: this gate lives in the ROOT layout, which mounts ONCE
 * — including on the `/login` page, where the user isn't
 * authenticated yet. If we hydrated there, `/api/prefs` would 401 /
 * redirect and we'd get nothing — and since the root layout never
 * remounts, we'd never retry after login. So:
 *   - On `/login` → render immediately, do NOT hydrate.
 *   - On any app route → hydrate once (the `usePathname()` change
 *     after login re-fires the effect), blocking render until done.
 *
 * The block is brief (one tiny DB-backed request) and bounded by a
 * timeout — if the server is slow we render anyway after ~1.2 s and
 * run on whatever local copy exists.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import dynamic from "@/lib/spa/dynamic";
import { usePathname } from "@/lib/spa/navigation";
import { hydratePrefs, installPrefsSync } from "@/lib/prefsSync";
import { seedDefaultLayoutIfFresh } from "@/lib/startup/defaultLayout";
import { installDisplayProfiles, applyDisplayProfilesAfterHydrate } from "@/lib/display/profiles";
import { ensureRetinaArrangedProfile } from "@/lib/display/retinaArrangedProfile";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { ChartQuickLookHost } from "@/components/chart/ChartQuickLook";

const AdvancedDeskKeepAliveHost = dynamic(
  () =>
    import("@/components/chart/advanced/AdvancedDeskKeepAlive").then(
      (m) => m.AdvancedDeskKeepAliveHost,
    ),
  { ssr: false },
);

const HYDRATE_TIMEOUT_MS = 1200;

function isAuthRoute(pathname: string | null): boolean {
  return !!pathname && pathname.startsWith("/login");
}

export function PrefsSyncGate({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const onAuthRoute = isAuthRoute(pathname);
  const [hydrated, setHydrated] = useState(false);
  // Guards against re-hydrating on every in-app navigation — we only
  // need the server pull once per page-load session.
  const startedRef = useRef(false);

  useEffect(() => {
    // On the login page, don't touch the prefs API (unauthenticated).
    if (onAuthRoute) return;
    // Install the layout-namespace patch FIRST — before installPrefsSync (below)
    // and before any workspace mounts and patches setItem — so every layout
    // read/write lands in the active Display Profile's slot. Idempotent. The
    // migration/activation waits until AFTER hydrate (finish, below), when the
    // synced profile definitions have been pulled into localStorage. Guarded so
    // profile setup can never throw out and leave the render gate closed (blank).
    try { installDisplayProfiles(); } catch { /* render un-namespaced */ }
    if (startedRef.current) return;
    startedRef.current = true;

    let done = false;
    const finish = (didHydrate: boolean) => {
      if (done) return;
      done = true;
      installPrefsSync();
      // Now that hydrate has pulled the synced profile definitions into
      // localStorage, run the one-time copy→namespace migration and point
      // storage at this machine's active Display Profile — before the layout
      // components mount below. No-ops (without flagging) if profiles aren't
      // present yet, so a hydrate timeout simply retries next load. Guarded so
      // it can never prevent setHydrated() below from lifting the render gate.
      try { applyDisplayProfilesAfterHydrate(); } catch { /* render anyway */ }
      try { ensureRetinaArrangedProfile(); } catch { /* clone is convenience */ }
      // Unblock the workspace immediately. Seeding a fresh profile used to
      // sit behind a 500 KB lazy JSON import and leave Charting a blank
      // canvas. Returning users skip the snapshot; a first-run seed can
      // land after first paint.
      setHydrated(true);
      if (didHydrate) void seedDefaultLayoutIfFresh();
    };
    const timer = setTimeout(() => finish(false), HYDRATE_TIMEOUT_MS);
    hydratePrefs().finally(() => {
      clearTimeout(timer);
      finish(true);
    });
    return () => clearTimeout(timer);
    // onAuthRoute is the dep: when it flips false (login → app route)
    // the effect re-fires and hydration runs for the first time.
  }, [onAuthRoute]);

  // Login page renders immediately — never gated. Theme still wraps so
  // /login can call useTheme without a second provider tree.
  if (onAuthRoute) return <ThemeProvider>{children}</ThemeProvider>;
  // App routes wait for the server pull so workspaces read hydrated
  // localStorage. Placeholder matches the canvas so there's no flash.
  if (!hydrated) return <div className="min-h-screen bg-canvas" aria-hidden />;
  // Keep-alive portals keep React context from THIS tree, not the visible
  // slot. AdvancedChartPane / useChartChrome call useTheme() and throw
  // "must be used within ThemeProvider" if the host is a sibling of it —
  // that error has no boundary and whitescreens the whole app. The quick
  // look's window (one per app, any page) draws a chart, so it sits here too.
  return (
    <ThemeProvider>
      {children}
      <AdvancedDeskKeepAliveHost />
      <ChartQuickLookHost />
    </ThemeProvider>
  );
}
